/**
 * EAA-1988: "this cơ sở is being renamed right now" — app-wide.
 *
 * While ACH renames a tenant's slug it holds that tenant offline, so every
 * request answers 409 with `ERROR_TENANT_SLUG_RENAME_IN_PROGRESS`. Without this
 * the user would see a generic error on whatever screen they happened to be on,
 * with no way to tell a transient failure from a planned operation.
 *
 * The state is set from the API error middleware rather than from a screen,
 * because the refusal can arrive on any request — there is no one page that owns
 * it. It clears on the first successful response, which is also how the app
 * notices the rename finished without polling.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

/**
 * What ACH tells us about the rename.
 *
 * The same shape arrives two ways — in the 409 body's `details`, and as
 * `slug_rename` on `GET /tenants/me`, which is the one endpoint that stays
 * reachable during the lockdown. The backend builds both from one reader, so a
 * poll landing between two refusals cannot make the bar jump backwards.
 */
export interface TenantRenameInfo {
    jobId: string | null;
    status: string;
    phase: string;
    oldSlug: string;
    newSlug: string;
    /** Every phase in the run, in order — the bar's step labels. */
    phases: string[];
    phasesTotal: number;
    phasesDone: number;
    /** Rows looked at and rows rewritten so far, across all phases. */
    examined: number;
    written: number;
    /** Rows the job counted when it opened; 0 when it never measured. */
    total: number;
    /** 0–100. Derived server-side so every client agrees. */
    percent: number;
}

/** Read one of ACH's `details` / `slug_rename` payloads into the store's shape. */
export function parseTenantRenameInfo(raw: Record<string, unknown>): TenantRenameInfo {
    const num = (key: string) => {
        const value = Number(raw[key]);
        return Number.isFinite(value) ? value : 0;
    };
    const text = (key: string) => (typeof raw[key] === 'string' ? (raw[key] as string) : '');
    return {
        jobId: typeof raw.job_id === 'string' ? raw.job_id : null,
        status: text('status'),
        phase: text('phase'),
        oldSlug: text('old_slug'),
        newSlug: text('new_slug'),
        phases: Array.isArray(raw.phases) ? (raw.phases as string[]) : [],
        phasesTotal: num('phases_total'),
        phasesDone: num('phases_done'),
        examined: num('examined'),
        written: num('written'),
        total: num('total'),
        percent: Math.max(0, Math.min(100, num('percent'))),
    };
}

interface TenantRenameStore {
    /** Non-null only while the tenant is locked. */
    rename: TenantRenameInfo | null;
    /** When the block was first seen — used to show elapsed time, not to decide. */
    blockedSince: number | null;
    startBlocking: (info: TenantRenameInfo) => void;
    clearBlocking: () => void;
}

export const useTenantRenameStore = create<TenantRenameStore>()(
    devtools(
        (set, get) => ({
            rename: null,
            blockedSince: null,
            startBlocking: (info) => {
                // Keep the original blockedSince across repeated refusals so the
                // elapsed time reflects the outage, not the latest failed request.
                const existing = get().blockedSince;
                // Never let the bar run backwards. Refusals and polls interleave,
                // and an older reply arriving late would otherwise redraw progress
                // the user has already watched go past — which reads as a restart.
                const previous = get().rename;
                const next =
                    previous && previous.jobId === info.jobId && previous.percent > info.percent
                        ? { ...info, percent: previous.percent }
                        : info;
                set(
                    { rename: next, blockedSince: existing ?? Date.now() },
                    false,
                    'tenantRename/startBlocking'
                );
            },
            clearBlocking: () => {
                if (get().rename === null) return;
                set({ rename: null, blockedSince: null }, false, 'tenantRename/clearBlocking');
            },
        }),
        { name: 'tenantRename' }
    )
);

/**
 * True while the cơ sở is locked for a rename.
 *
 * Pages use this to stay quiet: during a lockdown every request fails, so each
 * screen's own "could not load" toast fires on top of an overlay that already
 * explains why — noise stacked on the answer. Read outside React on purpose, so
 * a `catch` block can ask without becoming a hook.
 */
export function isTenantRenameBlocking(): boolean {
    return useTenantRenameStore.getState().rename !== null;
}
